import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import {
  AppConfigService,
  AppTokenService,
  AuthAccessTokenDetails,
  DashboardResponse,
  UserSessionService
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { SessionHelperService } from './session-helper.service';
import { SessionTokenHelper } from './session-token.helper';
import { LoginType, OAuthFeatureConfig } from '../../models/session.interfaces';

/**
 * Session Service for managing user authentication state.
 *
 * This service uses UserSessionService for dashboard data (shared with header)
 * and AppTokenService for app-specific token management.
 *
 * Usage:
 * 1. Provide OAUTH_FEATURE_CONFIG with app and endpointKey (for token fetching)
 * 2. Provide REDIRECT (default: true) and ACCESSIBLE (default: false)
 * 3. Use loggedIn$ observable in guards or components
 *
 * Example:
 * {
 *   provide: 'OAUTH_FEATURE_CONFIG',
 *   useValue: { app: 'APP_NAME', endpointKey: 'AUTH_CONFIG_KEY' }
 * }
 */
@Injectable()
export class SessionService extends SessionHelperService implements OnDestroy {
  /** Observable of user dashboard data */
  userData$: Observable<Partial<DashboardResponse>>;

  private _subscription: Subscription = new Subscription();
  private _token!: AuthAccessTokenDetails;
  private _validationInProgress = false;
  private _tokenHelper: SessionTokenHelper;

  /** Public getter for the access token */
  get token(): AuthAccessTokenDetails {
    return this._token;
  }

  constructor(
    @Optional() @Inject('LOGIN_TYPE') private _loginType: LoginType,
    @Optional() @Inject('REDIRECT') protected _overrideRedirect: boolean,
    @Optional() @Inject('ACCESSIBLE') protected _accessible: boolean,
    @Optional()
    @Inject('OAUTH_FEATURE_CONFIG')
    private _oAuthConfig: OAuthFeatureConfig,
    private appTokenService: AppTokenService,
    private userSessionService: UserSessionService,
    private http: HttpClient,
    protected override appConfig: AppConfigService
  ) {
    super(appConfig);

    this._tokenHelper = new SessionTokenHelper(
      this._oAuthConfig,
      this.appTokenService,
      this.http,
      this.appConfig
    );

    // Determine redirect behavior
    this._redirect =
      this._overrideRedirect !== null
        ? this._overrideRedirect
        : this._loginType !== 'modal';

    this.userData$ = this._dashboardData$.asObservable();

    // Initialize session validation if accessible or redirect is not explicitly disabled
    if (!(_overrideRedirect === false) || _accessible) {
      this.validateUserSession();
    }
  }

  /**
   * Validates the user session by checking token and fetching dashboard data.
   */
  async validateUserSession(
    loginRedirect = false,
    modal = false
  ): Promise<void> {
    // Prevent duplicate validation calls
    if (this._validationInProgress) {
      return;
    }
    this._validationInProgress = true;

    try {
      if (this._loginType === 'modal' && !modal) {
        this.getDashboardContent();
      } else {
        await this.validateTokenAndFetchDashboard(loginRedirect);
      }
    } finally {
      this._validationInProgress = false;
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  /**
   * Validates the access token and fetches dashboard or redirects.
   */
  private async validateTokenAndFetchDashboard(
    loginRedirect: boolean
  ): Promise<void> {
    this._token = await this._tokenHelper.getAccessToken();
    if (this._tokenHelper.checkTokenValue(this._token)) {
      this.getDashboardContent();
    } else {
      this.validateRedirect(false, loginRedirect);
    }
  }

  /**
   * Fetches dashboard content using UserSessionService (shared with header).
   * This ensures only one getDashBrdData call is made across the application.
   */
  private getDashboardContent(): void {
    this._subscription.unsubscribe();
    this._subscription = this.userSessionService.getUserSession().subscribe({
      next: (response) => this.getLoginStatus(response),
      error: () => {
        if (this._redirect) {
          this.redirect();
        }
      }
    });
  }

  /**
   * Determines login status from dashboard response.
   */
  private getLoginStatus(response: Partial<DashboardResponse>): boolean {
    const status = response.loggedIn ? response.loggedIn : false;
    this.validateRedirect(status);
    this.setResponseValues(response, status);
    return status;
  }
}
